import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, Info, XCircle } from 'lucide-react';
import { Modal } from '../Modal';
import { Botao } from './Botao';
import css from './Sobreposicao.module.css';
import { cx } from './cx';

/* ── Confirmação ────────────────────────────────────────────────────────── */

export interface PedidoConfirmacao {
  titulo: string;
  mensagem: ReactNode;
  /** Texto do botão de confirmar ("Apagar busca"). Diga o que acontece. */
  confirmar?: string;
  cancelar?: string;
  /** Ação destrutiva: botão vermelho e ícone de alerta. */
  perigo?: boolean;
}

type Confirmar = (p: PedidoConfirmacao) => Promise<boolean>;

const ContextoConfirmacao = createContext<Confirmar | null>(null);

/**
 * `const confirmar = useConfirmar(); if (!(await confirmar({...}))) return;`
 *
 * Substitui o `window.confirm`: aquele trava a página, não segue a identidade
 * visual e mostra o endereço do site como título — parece aviso de golpe.
 */
export function useConfirmar(): Confirmar {
  const ctx = useContext(ContextoConfirmacao);
  if (!ctx) throw new Error('useConfirmar fora do <ProvedorSobreposicoes>.');
  return ctx;
}

/* ── Notificações ───────────────────────────────────────────────────────── */

type TomNotificacao = 'ok' | 'erro' | 'info';
interface Notificacao {
  id: number;
  tom: TomNotificacao;
  texto: ReactNode;
}
type Notificar = (texto: ReactNode, tom?: TomNotificacao) => void;

const ContextoNotificacao = createContext<Notificar | null>(null);

/** `notificar('Busca apagada.')` — mensagem curta que some sozinha. */
export function useNotificar(): Notificar {
  const ctx = useContext(ContextoNotificacao);
  if (!ctx) throw new Error('useNotificar fora do <ProvedorSobreposicoes>.');
  return ctx;
}

const ICONE_NOTIFICACAO = { ok: CheckCircle2, erro: XCircle, info: Info } as const;

export function ProvedorSobreposicoes({ children }: { children: ReactNode }) {
  const [pedido, setPedido] = useState<PedidoConfirmacao | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const seq = useRef(0);

  const confirmar = useCallback<Confirmar>((p) => {
    resolver.current?.(false);
    setPedido(p);
    return new Promise<boolean>((r) => {
      resolver.current = r;
    });
  }, []);

  const responder = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setPedido(null);
  };

  const notificar = useCallback<Notificar>((texto, tom = 'ok') => {
    const id = ++seq.current;
    setNotificacoes((n) => [...n.slice(-3), { id, tom, texto }]);
    window.setTimeout(
      () => setNotificacoes((n) => n.filter((x) => x.id !== id)),
      tom === 'erro' ? 7000 : 4000,
    );
  }, []);

  return (
    <ContextoConfirmacao.Provider value={confirmar}>
      <ContextoNotificacao.Provider value={notificar}>
        {children}

        <Modal
          aberto={pedido !== null}
          aoFechar={() => responder(false)}
          titulo={pedido?.titulo ?? ''}
          largura={460}
          rodape={
            <>
              <Botao aparencia="secundario" onClick={() => responder(false)}>
                {pedido?.cancelar ?? 'Cancelar'}
              </Botao>
              <Botao
                aparencia={pedido?.perigo ? 'perigoSolido' : 'primario'}
                onClick={() => responder(true)}
                autoFocus
              >
                {pedido?.confirmar ?? 'Confirmar'}
              </Botao>
            </>
          }
        >
          <div className={css.confirmacao}>
            <span
              className={cx(css.confirmacaoIcone, pedido?.perigo && css.perigo)}
              aria-hidden="true"
            >
              {pedido?.perigo ? <AlertTriangle /> : <HelpCircle />}
            </span>
            <div className={css.confirmacaoTexto}>{pedido?.mensagem}</div>
          </div>
        </Modal>

        <div className={css.notificacoes} aria-live="polite">
          {notificacoes.map((n) => {
            const Icone = ICONE_NOTIFICACAO[n.tom];
            return (
              <div key={n.id} className={cx(css.notificacao, css[n.tom])} role="status">
                <Icone aria-hidden="true" />
                <span className={css.notificacaoTexto}>{n.texto}</span>
              </div>
            );
          })}
        </div>
      </ContextoNotificacao.Provider>
    </ContextoConfirmacao.Provider>
  );
}

/* ── Menu suspenso ──────────────────────────────────────────────────────── */

/**
 * Menu que abre num botão (usuário no topo, "mais ações" numa linha). Fecha ao
 * clicar fora, no `Esc` e ao escolher um item.
 */
export function Menu({
  gatilho,
  cabecalho,
  alinhar = 'direita',
  children,
}: {
  /** Recebe as props de acessibilidade e o alternador. */
  gatilho: (p: {
    aberto: boolean;
    alternar: () => void;
    'aria-expanded': boolean;
    'aria-haspopup': 'menu';
  }) => ReactNode;
  cabecalho?: ReactNode;
  alinhar?: 'esquerda' | 'direita';
  children: ReactNode;
}) {
  const [posicao, setPosicao] = useState<CSSProperties | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const aberto = posicao !== null;

  // Posição FIXA, calculada a partir do botão: dentro de uma tabela com
  // rolagem horizontal, um menu `absolute` seria cortado pela borda dela.
  const abrir = () => {
    const r = raiz.current?.getBoundingClientRect();
    if (!r) return;
    const embaixo = window.innerHeight - r.bottom;
    const p: CSSProperties = { position: 'fixed' };
    if (embaixo < 260 && r.top > embaixo) p.bottom = window.innerHeight - r.top + 6;
    else p.top = r.bottom + 6;
    if (alinhar === 'direita') p.right = Math.max(8, window.innerWidth - r.right);
    else p.left = Math.max(8, r.left);
    setPosicao(p);
  };

  useEffect(() => {
    if (!aberto) return;
    const fechar = () => setPosicao(null);
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (!raiz.current?.contains(alvo) && !menu.current?.contains(alvo)) fechar();
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    // Posição fixa não acompanha a rolagem: fecha em vez de ficar solto.
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  return (
    <div className={css.menuRaiz} ref={raiz}>
      {gatilho(
        // `aberto` NÃO enumerável: o uso comum é `({ alternar, ...aria }) =>
        // <button {...aria}>`, e um `aberto` enumerável iria parar no DOM como
        // atributo (aviso do React). Desestruturar `aberto` continua funcionando.
        Object.defineProperty(
          {
            alternar: () => (aberto ? setPosicao(null) : abrir()),
            'aria-expanded': aberto,
            'aria-haspopup': 'menu' as const,
          },
          'aberto',
          { value: aberto, enumerable: false },
        ) as Parameters<typeof gatilho>[0],
      )}
      {aberto && (
        <div
          ref={menu}
          role="menu"
          className={css.menu}
          style={posicao}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('[role="menuitem"]')) setPosicao(null);
          }}
        >
          {cabecalho && <div className={css.menuCabecalho}>{cabecalho}</div>}
          {children}
        </div>
      )}
    </div>
  );
}

export function ItemMenu({
  icone,
  perigo,
  disabled,
  title,
  onClick,
  children,
}: {
  icone?: ReactNode;
  perigo?: boolean;
  disabled?: boolean;
  /** Explica por que está desabilitado. */
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cx(css.menuItem, perigo && css.menuItemPerigo)}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      title={title}
      onClick={onClick}
    >
      {icone}
      {children}
    </button>
  );
}

export function DivisorMenu() {
  return <div className={css.menuDivisor} role="separator" />;
}
